/*
 * 工具调用极简文案 — tool_name → { 执行中 / 执行完成 / 执行错误 }
 *
 * 用途:
 *   - prod / showToolDetails=false 时, message footer / body 极简模式只渲染最近一个工具的 label
 *   - 不在表中的工具走 genericLabel(toolName) 兜底, 不会 crash
 *
 * 维护:
 *   - 跟 XHeyClaw/src/agents/tools/ 下的 typed tool 目录名 (kebab-case → snake_case key) 对齐
 *   - 文案口径: 动词 + 业务对象, "正在 X" / "已 X" / "X 有问题"
 */

export type ToolStatus = 'running' | 'done' | 'error'

export interface ToolLabel {
  running: string
  done: string
  error: string
}

export const TOOL_STATUS_LABELS: Record<string, ToolLabel> = {
  // ─── 文档处理 ────────────────────────────────────────────────
  excel_read: {
    running: '正在读取表格',
    done: '已读取表格',
    error: '表格读取有问题'
  },
  excel_edit: {
    running: '正在编辑表格',
    done: '已编辑表格',
    error: '表格编辑有问题'
  },
  excel_batch_fill: {
    running: '正在批量填表',
    done: '已批量填表',
    error: '批量填表有问题'
  },
  excel_archive: {
    running: '正在归档表格',
    done: '已归档表格',
    error: '表格归档有问题'
  },
  excel_recalc: {
    running: '正在重算公式',
    done: '已重算公式',
    error: '公式重算有问题'
  },
  word_read: {
    running: '正在读取文档',
    done: '已读取文档',
    error: '文档读取有问题'
  },
  word_edit: {
    running: '正在编辑文档',
    done: '已编辑文档',
    error: '文档编辑有问题'
  },
  word_compose: {
    running: '正在生成文档',
    done: '已生成文档',
    error: '文档生成有问题'
  },
  word_fill_template: {
    running: '正在填模板',
    done: '已填模板',
    error: '模板填写有问题'
  },
  word_template_inspect: {
    running: '正在分析模板',
    done: '已分析模板',
    error: '模板分析有问题'
  },
  word_template_bind: {
    running: '正在绑定模板',
    done: '已绑定模板',
    error: '模板绑定有问题'
  },
  word_template_to_ops: {
    running: '正在转换模板',
    done: '已转换模板',
    error: '模板转换有问题'
  },
  // 文档模板 CRUD (D-055 control plane) — 资料员对外口径"模板", 不暴露 skill 概念
  doc_template_create: {
    running: '正在保存模板',
    done: '已保存模板',
    error: '模板保存有问题'
  },
  doc_template_read: {
    running: '正在读取模板',
    done: '已读取模板',
    error: '模板读取有问题'
  },
  doc_template_list: {
    running: '正在列模板',
    done: '已列模板',
    error: '模板列出有问题'
  },
  doc_template_delete: {
    running: '正在删除模板',
    done: '已删除模板',
    error: '模板删除有问题'
  },
  // OOXML 内嵌图片解压 — 配合 image_extract_info 提取水印 / EXIF
  doc_image_extract: {
    running: '正在提取文档图片',
    done: '已提取文档图片',
    error: '文档图片提取有问题'
  },
  ppt_compose: {
    running: '正在生成 PPT',
    done: '已生成 PPT',
    error: 'PPT 生成有问题'
  },
  ppt_inventory: {
    running: '正在盘点 PPT',
    done: '已盘点 PPT',
    error: 'PPT 盘点有问题'
  },
  ppt_rearrange: {
    running: '正在重排 PPT',
    done: '已重排 PPT',
    error: 'PPT 重排有问题'
  },
  ppt_replace: {
    running: '正在替换 PPT 内容',
    done: '已替换 PPT 内容',
    error: 'PPT 替换有问题'
  },
  ppt_thumbnail: {
    running: '正在生成缩略图',
    done: '已生成缩略图',
    error: '缩略图生成有问题'
  },
  pdf: { running: '正在处理 PDF', done: '已处理 PDF', error: 'PDF 处理有问题' },
  pdf_render: {
    running: '正在渲染 PDF',
    done: '已渲染 PDF',
    error: 'PDF 渲染有问题'
  },
  pdf_to_image: {
    running: '正在转 PDF 为图片',
    done: '已转 PDF 为图片',
    error: 'PDF 转图有问题'
  },
  pdf_form_check: {
    running: '正在检查 PDF 表单',
    done: '已检查 PDF 表单',
    error: 'PDF 表单检查有问题'
  },
  pdf_form_extract: {
    running: '正在提取 PDF 字段',
    done: '已提取 PDF 字段',
    error: 'PDF 字段提取有问题'
  },
  pdf_form_fill_annotations: {
    running: '正在填 PDF 标注',
    done: '已填 PDF 标注',
    error: 'PDF 标注填写有问题'
  },
  pdf_form_fill_text: {
    running: '正在填 PDF 文本',
    done: '已填 PDF 文本',
    error: 'PDF 文本填写有问题'
  },
  pdf_form_preview: {
    running: '正在预览 PDF',
    done: '已预览 PDF',
    error: 'PDF 预览有问题'
  },
  pdf_form_validate: {
    running: '正在校验 PDF',
    done: '已校验 PDF',
    error: 'PDF 校验有问题'
  },

  // ─── 业务查询 ────────────────────────────────────────────────
  watermark_search: {
    running: '正在查水印台账',
    done: '已查水印台账',
    error: '水印台账查询有问题'
  },
  watermark_list: {
    running: '正在列水印',
    done: '已列水印',
    error: '水印列出有问题'
  },
  watermark_titles: {
    running: '正在取水印标题',
    done: '已取水印标题',
    error: '水印标题获取有问题'
  },
  watermark_export: {
    running: '正在导出水印',
    done: '已导出水印',
    error: '水印导出有问题'
  },
  photo_search: {
    running: '正在查照片',
    done: '已查照片',
    error: '照片查询有问题'
  },
  photo_export_excel: {
    running: '正在导出照片表格',
    done: '已导出照片表格',
    error: '照片表格导出有问题'
  },
  photo_export_zip: {
    running: '正在打包照片',
    done: '已打包照片',
    error: '照片打包有问题'
  },
  photo_export_titles: {
    running: '正在导出照片标题',
    done: '已导出照片标题',
    error: '照片标题导出有问题'
  },
  photo_download_status: {
    running: '正在查下载状态',
    done: '已查下载状态',
    error: '下载状态查询有问题'
  },
  customer_search: {
    running: '正在查客户',
    done: '已查客户',
    error: '客户查询有问题'
  },
  customer_list: {
    running: '正在列客户',
    done: '已列客户',
    error: '客户列出有问题'
  },
  customer_visits: {
    running: '正在查拜访记录',
    done: '已查拜访记录',
    error: '拜访记录查询有问题'
  },
  customer_export: {
    running: '正在导出客户',
    done: '已导出客户',
    error: '客户导出有问题'
  },
  customer_nearby: {
    running: '正在查附近客户',
    done: '已查附近客户',
    error: '附近客户查询有问题'
  },
  attendance_day: {
    running: '正在查考勤明细',
    done: '已查考勤明细',
    error: '考勤明细查询有问题'
  },
  attendance_stat: {
    running: '正在统计考勤',
    done: '已统计考勤',
    error: '考勤统计有问题'
  },
  attendance_export: {
    running: '正在导出考勤',
    done: '已导出考勤',
    error: '考勤导出有问题'
  },
  attendance_photo: {
    running: '正在查考勤照片',
    done: '已查考勤照片',
    error: '考勤照片查询有问题'
  },
  attendance_rules: {
    running: '正在读考勤规则',
    done: '已读考勤规则',
    error: '考勤规则读取有问题'
  },
  folder_search: {
    running: '正在查文件夹',
    done: '已查文件夹',
    error: '文件夹查询有问题'
  },
  folder_list: {
    running: '正在列文件夹',
    done: '已列文件夹',
    error: '文件夹列出有问题'
  },
  folder_photos: {
    running: '正在查文件夹照片',
    done: '已查文件夹照片',
    error: '文件夹照片查询有问题'
  },
  folder_export: {
    running: '正在导出文件夹',
    done: '已导出文件夹',
    error: '文件夹导出有问题'
  },
  user_get: {
    running: '正在查用户',
    done: '已查用户',
    error: '用户查询有问题'
  },
  user_search: {
    running: '正在搜索用户',
    done: '已搜索用户',
    error: '用户搜索有问题'
  },
  team_departments: {
    running: '正在读部门结构',
    done: '已读部门结构',
    error: '部门结构读取有问题'
  },
  team_tree: {
    running: '正在读组织树',
    done: '已读组织树',
    error: '组织树读取有问题'
  },
  current_team: {
    running: '正在查当前团队',
    done: '已查当前团队',
    error: '当前团队查询有问题'
  },
  current_user: {
    running: '正在查当前用户',
    done: '已查当前用户',
    error: '当前用户查询有问题'
  },
  tag_list: {
    running: '正在列标签',
    done: '已列标签',
    error: '标签列出有问题'
  },
  tag_search: {
    running: '正在查标签',
    done: '已查标签',
    error: '标签查询有问题'
  },
  verify_photos: {
    running: '正在核验照片',
    done: '已核验照片',
    error: '照片核验有问题'
  },

  // ─── 图像 ────────────────────────────────────────────────────
  image_extract_info: {
    running: '正在提取图片信息',
    done: '已提取图片信息',
    error: '图片信息提取有问题'
  },
  image_extract_video_info: {
    running: '正在提取视频信息',
    done: '已提取视频信息',
    error: '视频信息提取有问题'
  },
  image_remove_watermark: {
    running: '正在去水印',
    done: '已去水印',
    error: '去水印有问题'
  },
  image_tool: {
    running: '正在处理图片',
    done: '已处理图片',
    error: '图片处理有问题'
  },
  jigsaw_render: { running: '正在拼图', done: '已拼图', error: '拼图有问题' },
  slideshow_render: {
    running: '正在生成幻灯片',
    done: '已生成幻灯片',
    error: '幻灯片生成有问题'
  },

  // ─── 文件系统 ────────────────────────────────────────────────
  ls: { running: '正在列目录', done: '已列目录', error: '目录列出有问题' },
  find: {
    running: '正在查找文件',
    done: '已查找文件',
    error: '文件查找有问题'
  },
  grep: {
    running: '正在搜索内容',
    done: '已搜索内容',
    error: '内容搜索有问题'
  },
  read: { running: '正在读文件', done: '已读文件', error: '文件读取有问题' },
  write: { running: '正在写文件', done: '已写文件', error: '文件写入有问题' },
  edit: {
    running: '正在编辑文件',
    done: '已编辑文件',
    error: '文件编辑有问题'
  },
  // bash/exec 对用户口径统一为"执行命令"(不暴露 Bash/shell 这类技术词)
  bash: {
    running: '正在执行命令',
    done: '已执行命令',
    error: '命令执行有问题'
  },
  exec: {
    running: '正在执行命令',
    done: '已执行命令',
    error: '命令执行有问题'
  },

  // ─── 数据 / 分析 ──────────────────────────────────────────────
  dataframe_query: {
    running: '正在查数据',
    done: '已查数据',
    error: '数据查询有问题'
  },
  stats: {
    running: '正在统计数据',
    done: '已统计数据',
    error: '数据统计有问题'
  },

  // ─── session / 工作流 ────────────────────────────────────────
  session_notes: {
    running: '正在更新会话笔记',
    done: '已更新会话笔记',
    error: '会话笔记更新有问题'
  },
  session_tool_cache_read: {
    running: '正在读缓存',
    done: '已读缓存',
    error: '缓存读取有问题'
  },
  sessions_send: {
    running: '正在发送子任务消息',
    done: '已发送子任务消息',
    error: '子任务消息发送有问题'
  },
  sessions_spawn: {
    running: '正在启动子任务',
    done: '已启动子任务',
    error: '子任务启动有问题'
  },
  workspace_current_doc: {
    running: '正在读当前文档',
    done: '已读当前文档',
    error: '当前文档读取有问题'
  },

  // ─── xhey 内部 ───────────────────────────────────────────────
  xhey_memory: {
    running: '正在读记忆',
    done: '已读记忆',
    error: '记忆读取有问题'
  },
  xhey_skill: {
    running: '正在加载技能',
    done: '已加载技能',
    error: '技能加载有问题'
  },
  // 资料员场景对外口径"模板", 不暴露底层 skill 概念 (配套 platform/AGENTS.md § 6)
  register_team_skill: {
    running: '正在保存模板',
    done: '已保存模板',
    error: '模板保存有问题'
  },
  xhey_spill_test: {
    running: '正在测试溢出',
    done: '已测试溢出',
    error: '溢出测试有问题'
  },
  xhey_time: {
    running: '正在获取时间',
    done: '已获取时间',
    error: '时间获取有问题'
  },
  xhey_version: {
    running: '正在查版本',
    done: '已查版本',
    error: '版本查询有问题'
  },

  // ─── 其他 ────────────────────────────────────────────────────
  cron: {
    running: '正在配置定时',
    done: '已配置定时',
    error: '定时配置有问题'
  },
  oss_download: {
    running: '正在下载文件',
    done: '已下载文件',
    error: '文件下载有问题'
  },
  oss_upload: {
    running: '正在上传文件',
    done: '已上传文件',
    error: '文件上传有问题'
  },
  llm_invoke: {
    running: '正在调用 AI',
    done: 'AI 已应答',
    error: 'AI 调用有问题'
  },
  skill: {
    running: '正在调用技能',
    done: '已调用技能',
    error: '技能调用有问题'
  },
  memory: {
    running: '正在读取记忆',
    done: '已读取记忆',
    error: '记忆读取有问题'
  },
  nodes: {
    running: '正在处理节点',
    done: '已处理节点',
    error: '节点处理有问题'
  }
}

/** 兜底文案 — 不在 mapping 里的工具用 generic 模板, 不让 UI 露 raw tool name */
function genericLabel(toolName: string, status: ToolStatus): string {
  const friendly = toolName.replace(/_/g, ' ')
  switch (status) {
    case 'running':
      return `正在调用 ${friendly}`
    case 'done':
      return `已调用 ${friendly}`
    case 'error':
      return `${friendly} 调用有问题`
  }
}

export function getToolStatusLabel(
  toolName: string,
  status: ToolStatus
): string {
  const entry = TOOL_STATUS_LABELS[toolName]
  if (entry) return entry[status]
  return genericLabel(toolName, status)
}

/**
 * 工具的"名词"口径 — 用于工具组展开行的「xxx工具」标签, 不带动词/状态.
 *   - 在 mapping 里: 取 done 文案去掉前缀"已"得到动宾名词短语
 *     (如 "已读取表格" → "读取表格", "已编辑文档" → "编辑文档")
 *   - 不在 mapping 里: 把 toolName prettify + 首字母大写, 不露 raw slug
 *     (如 "bash" → "Bash", "oss_download" → "Oss download")
 */
export function getToolName(toolName: string): string {
  const entry = TOOL_STATUS_LABELS[toolName]
  if (entry) return entry.done.replace(/^已/, '').trim()
  const pretty = toolName.replace(/_/g, ' ').trim()
  return pretty ? pretty.charAt(0).toUpperCase() + pretty.slice(1) : toolName
}

/**
 * 把后端 activityDescription 截短并做质量过滤.
 *
 * 1. 去掉冒号后的路径/参数部分 (后端常下发 "{动作}: {长路径}" 格式)
 * 2. 检测参数泄露 / 全英文 / 工具名直出 等"不够人话"的 activityDescription,
 *    返回 undefined 让上层 fallback 到 TOOL_STATUS_LABELS 的中文模板.
 *    这样即使后端 prompt 没及时改, 前端也能挡掉 `数据查询 (op=filter)` 这类输出.
 */
export function shortenActivityDescription(
  text: string | undefined
): string | undefined {
  if (!text) return text
  // 兼容中英文冒号
  const idx = text.search(/[:：]/)
  const trimmed = idx >= 0 ? text.slice(0, idx).trim() : text.trim()
  if (!trimmed) return undefined
  // 质量过滤:
  //   - 含 "=" 形态 → 参数泄露 (e.g. "数据查询 (op=filter)")
  //   - 含 "(" / "(" → 业务化短语不该带括号 (e.g. "搜索水印记录 (2026-05-11 00"
  //     这种截断后括号未闭合, 或 "查询本周记录 (filter=...)" 参数泄露)
  //   - 含下划线 token (e.g. "dataframe_query") → 工具函数名直出
  //   - 全 ASCII (无中文) 且长度 >= 4 → 大概率英文工具名 (e.g. "Reading Excel")
  if (/[=]/.test(trimmed)) return undefined
  if (/[(（]/.test(trimmed)) return undefined
  if (/[a-z]_[a-z]/.test(trimmed)) return undefined
  if (trimmed.length >= 4 && !/[一-龥]/.test(trimmed)) return undefined
  return trimmed
}
